import React, { ChangeEvent, useEffect, useState } from "react";
import { BEST, DESCRIPTION, ENTER, FIELDS, INCOMPATIBLE, MULTI_CHOICE, NAME, RULE_EMPTY_STRING, RULE_FUNCTION_DESCRIPTION, RULE_FUNCTION_NAME, RULE_NAME_MANDATORY_MESSAGE, RULE_SAVE, RULE_SAVE_MESSAGE, RULE_SELECT, RULE_STAR, RULE_UPDATE_MESSAGE, SUB_MODEL_TYPE,} from "../../ISRConstants";
import { EmptyCircle, FilledCircle } from "assests/GmsIcons";
import { TConnector, TIndexState, TProductCategory, TCBMRule, TField, TSubmodel, TUpsertISRProjectDataModelsInput, } from "../DefineModel/model";
import MatrixTable from "./MatrixTable";
import { toast } from "react-toastify";
import { TAttributeChoice } from "types/Pages/ProductDelta";

type TCBMRuleProps = {
  selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
  setUserProjectDataModels: React.Dispatch<
    React.SetStateAction<TUpsertISRProjectDataModelsInput>
  >;
  userProjectDataModels: TUpsertISRProjectDataModelsInput;
  indexInfo?: TIndexState;
  editId?: any;
  ruleType: any;
  toggleMenu: () => void;
  createISRProject:(isRuleUpdate: boolean)=>void;
};

const CBMRule = ({
  userProjectDataModels,
  setUserProjectDataModels,
  selectedItem,
  indexInfo,
  ruleType,
  editId,
  toggleMenu,
  createISRProject,
}: TCBMRuleProps) => {
  // const [selectedRuleType, setSelectedRuleType] = useState<string>('');
  const [dataModels, setDataModels] = useState<TSubmodel[]>([]);

  const [selectedLeftDataModel, setSelectedLeftDataModel] =
    useState<TSubmodel | null>(null);
  const [selectedRightDataModel, setSelectedRightDataModel] =
    useState<TSubmodel | null>(null);

  const [leftSideFields, setLeftSideFields] = useState<TField[]>([]);
  const [rightSideFields, setRightSideFields] = useState<TField[]>([]);

  const [selectedLeftSideField, setSelectedLeftSideField] =
    useState<TField | null>(null);
  const [selectedRightSideField, setSelectedRightSideField] =
    useState<TField | null>(null);

  const [selectedLeftSideAttributeValues, setSelectedLeftSideAttributeValues] =
    useState<TAttributeChoice[]>([]);
  const [
    selectedRightSideAttributeValues,
    setSelectedRightSideAttributeValues,
  ] = useState<TAttributeChoice[]>([]);
  const initialCbmRule: TCBMRule = {
    defaultValue: null,
    isCbm: true,
    operator: RULE_EMPTY_STRING,
    dependsOn: RULE_EMPTY_STRING,
    cbm: {},
    name: RULE_EMPTY_STRING,
    description: RULE_EMPTY_STRING,
    type: ruleType,
  };
  const [cbmRule, setCbmRule] = useState<TCBMRule>(initialCbmRule);
  const [ruleName, setRuleName] = useState<string | undefined>(
    RULE_EMPTY_STRING
  );

  const onInputChangeHandler = (e: any, type: string) => {
    setCbmRule((prevCbmRule) => ({
      ...prevCbmRule,
      [type]: e.target.value,
    }));
    if (type === "name") setRuleName(e.target.value);
  };

  useEffect(() => {
    if (editId !== null) {
      fetchEditState();
    }
  }, [selectedItem]);

  useEffect(() => {
    getDataModels();
  });

  useEffect(() => {
    if (editId === null) {
      initializeCBMState();
    }
  }, [selectedLeftSideField, selectedRightSideField]);

  const initializeCBMState = () => {
    setCbmRule((prev) => {
      let cbmMatrixObj = {
        [`${selectedLeftSideField?.id}`]: [
          ...selectedLeftSideAttributeValues?.map((col: any) => col?.value),
        ],
      };
      selectedRightSideAttributeValues.forEach((value: any) => {
        cbmMatrixObj[value.value] = Array(
          selectedLeftSideAttributeValues.length
        ).fill(0);
      });
      return { ...prev, cbm: cbmMatrixObj };
    });
  };

  const fetchEditState = () => {
    let prevRules;
    if (
      selectedItem &&
      "belongsToDataModel" in selectedItem &&
      selectedItem?.belongsToDataModel
    ) {
      prevRules =
        userProjectDataModels?.project.dataModels[
          indexInfo?.dataModelIndex ?? -1
        ]?.data.fields[indexInfo?.fieldIndex ?? -1]?.rules ?? [];
    } else {
      prevRules =
        userProjectDataModels?.project.fields[
          indexInfo?.fieldOutsideDataModelIndex ?? -1
        ]?.data?.rules ?? [];
    }
    if (Array.isArray(prevRules)) {
      const ruleObj = prevRules.filter((rule: any) => {
        return rule.id === editId;
      });

      if (ruleObj.length > 0) {
        const rule = ruleObj[0];
        setCbmRule(rule as TCBMRule);
        const localSelectedLeftSideSubModel =
          userProjectDataModels?.project?.dataModels?.filter(
            (dataModel) => dataModel.id === rule?.selectedLeftSideSubModel
          );
        const localSelectedRightSideSubModel =
          userProjectDataModels?.project?.dataModels?.filter(
            (dataModel) => dataModel.id === rule?.selectedRightSideSubModel
          );
        const multiLeftChoiceFields =
          localSelectedLeftSideSubModel[0]?.data?.fields?.filter(
            (field) => field.type === MULTI_CHOICE
          );
        const multiRightChoiceFields =
          localSelectedRightSideSubModel[0]?.data?.fields?.filter(
            (field) => field.type === MULTI_CHOICE
          );
        const localSelectedLeftSideField = multiLeftChoiceFields?.filter(
          (field) => field.id === rule?.selectedLeftSideField
        );
        const localSelectedRightSideField = multiRightChoiceFields?.filter(
          (field) => field.id === rule?.selectedRightSideField
        );
        setSelectedLeftDataModel(
          localSelectedLeftSideSubModel?.length > 0
            ? localSelectedLeftSideSubModel[0]
            : null
        );
        setSelectedRightDataModel(
          localSelectedRightSideSubModel?.length > 0
            ? localSelectedRightSideSubModel[0]
            : null
        );
        setLeftSideFields(multiLeftChoiceFields as TField[]);
        setRightSideFields(multiRightChoiceFields as TField[]);
        setSelectedLeftSideField(
          localSelectedLeftSideField?.length > 0
            ? localSelectedLeftSideField[0]
            : null
        );
        setSelectedRightSideField(
          localSelectedRightSideField?.length > 0
            ? localSelectedRightSideField[0]
            : null
        );
        const leftAttributeValues: TAttributeChoice[] =
          (localSelectedLeftSideField?.length
            ? localSelectedLeftSideField[0]
            : null
          )?.dataConfiguration?.choices?.map((choice) => ({
            id: choice.av_id ?? choice.id,
            value: choice.av_value ?? choice.value,
          })) ?? [];
        setSelectedLeftSideAttributeValues(leftAttributeValues);
        const rightAttributeValues: TAttributeChoice[] =
          (localSelectedRightSideField?.length > 0
            ? localSelectedRightSideField[0]
            : null
          )?.dataConfiguration?.choices?.map((choice) => ({
            id: choice.av_id ?? choice.id,
            value: choice.av_value ?? choice.value,
          })) ?? [];
        setSelectedRightSideAttributeValues(rightAttributeValues);
        setRuleName(rule?.name);
      }
    }
  };

  const handleLeftDataModelSelect = (selectedId: string) => {
    const selectedModel =
      dataModels.find((model) => model.id === selectedId) || null;
    setSelectedLeftDataModel(selectedModel);
    setCbmRule((prevCbmRule) => ({
      ...prevCbmRule,
      selectedLeftSideSubModel: selectedModel?.id ?? null,
    }));

    if (selectedModel) {
      const multiChoiceFields = selectedModel.data.fields.filter(
        (field) => field.type === MULTI_CHOICE
      );
      setLeftSideFields(multiChoiceFields);
    } else {
      setLeftSideFields([]);
    }
  };

  const handleRightDataModelSelect = (selectedId: string) => {
    const selectedModel =
      dataModels.find((model) => model.id === selectedId) || null;
    setSelectedRightDataModel(selectedModel);
    setCbmRule((prevCbmRule) => ({
      ...prevCbmRule,
      selectedRightSideSubModel: selectedModel?.id ?? null,
    }));

    if (selectedModel) {
      const multiChoiceFields = selectedModel.data.fields.filter(
        (field) => field.type === MULTI_CHOICE
      );
      setRightSideFields(multiChoiceFields);
    } else {
      setRightSideFields([]);
    }
  };

  const handleLeftSideFieldSelect = (selectedFieldId: string) => {
    const field =
      leftSideFields.find((field) => field.id === selectedFieldId) || null;
    setCbmRule((prevCbmRule) => ({
      ...prevCbmRule,
      dependsOn: field?.id ?? RULE_EMPTY_STRING,
      selectedLeftSideField: field?.id,
    }));
    if (field) {
      const attributeValues: TAttributeChoice[] =
        field.dataConfiguration?.choices?.map((choice) => ({
          id: choice.av_id ?? choice.id,
          value: choice.av_value ?? choice.value,
        })) ?? [];
      setSelectedLeftSideAttributeValues(attributeValues);
    }
    setSelectedLeftSideField(field);
  };

  const handleRightSideFieldSelect = (selectedFieldId: string) => {
    const field =
      rightSideFields.find((field) => field.id === selectedFieldId) || null;
    setSelectedRightSideField(field);
    setCbmRule((prevCbmRule) => ({
      ...prevCbmRule,
      dependsOn: field?.id ?? RULE_EMPTY_STRING,
      selectedRightSideField: field?.id,
    }));
    if (field) {
      const attributeValues: TAttributeChoice[] =
        field.dataConfiguration?.choices?.map((choice) => ({
          id: choice.av_id ?? choice.id,
          value: choice.av_value ?? choice.value,
        })) ?? [];
      setSelectedRightSideAttributeValues(attributeValues);
    }
  };

  const onSave = () => {
    let message = RULE_SAVE_MESSAGE;
    if (ruleName) {
      setUserProjectDataModels((prev) => {
        let newState: TUpsertISRProjectDataModelsInput = { ...prev };
        if (editId) {
          if (
            selectedItem &&
            "belongsToDataModel" in selectedItem &&
            selectedItem?.belongsToDataModel
          ) {
            newState.project.dataModels[
              (indexInfo as TIndexState).dataModelIndex as number
            ].data.fields[
              (indexInfo as TIndexState).fieldIndex as number
            ].rules = newState?.project.dataModels[
              indexInfo?.dataModelIndex ?? -1
            ]?.data.fields[indexInfo?.fieldIndex ?? -1]?.rules.map((rule) => {
              if (rule.id === editId) {
                rule = cbmRule;
              }
              return rule;
            });
          } else {
            newState.project.fields[
              (indexInfo as TIndexState).fieldOutsideDataModelIndex as number
            ].data.rules = newState?.project.fields[
              indexInfo?.fieldOutsideDataModelIndex ?? -1
            ]?.data?.rules.map((rule) => {
              if (rule.id === editId) {
                rule = cbmRule;
              }
              return rule;
            });
          }
          message = RULE_UPDATE_MESSAGE;
          return newState;
        } else {
          if (
            selectedItem &&
            "belongsToDataModel" in selectedItem &&
            selectedItem?.belongsToDataModel
          ) {
            newState?.project.dataModels[
              indexInfo?.dataModelIndex ?? -1
            ]?.data.fields[indexInfo?.fieldIndex ?? -1]?.rules.push(cbmRule);
          } else {
            newState?.project.fields[
              indexInfo?.fieldOutsideDataModelIndex ?? -1
            ]?.data?.rules.push(cbmRule);
          }
          return newState;
        }
      });
      createISRProject(true);
      setTimeout(() => {
        toast.success(message);
        toggleMenu();
      }, 1000);
    } else toast.info(RULE_NAME_MANDATORY_MESSAGE);
  };

  const getDataModels = () => {
    const extractedDataModels = userProjectDataModels?.project?.dataModels;
    setDataModels(extractedDataModels);
  };

  // const handleRuleTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
  //     const value = event.target.value;
  //     setSelectedRuleType(value);
  //     getDataModels();
  // };

  const handleOperatorChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    // setSelectedRuleType(value);
    setCbmRule((prevCbmRule) => ({
      ...prevCbmRule,
      operator: value,
    }));
  };

  return (
    <React.Fragment>
      <div className="tw-py-1 tw-bg-shades-of-grey-20 tw-p-6 ">
        <div className="tw-flex tw-items-center tw-gap-4">
          <div className="tw-w-[30%]">
            <label className="tw-text-sm tw-text-[#747593] tw-block">
              {NAME}
              <span className="tw-text-red-500 tw-ml-4">{RULE_STAR}</span>
            </label>
            <input
              className="input-field-dm"
              placeholder={ENTER}
              type="text"
              onChange={(e) => {
                onInputChangeHandler(e, RULE_FUNCTION_NAME);
              }}
              value={ruleName}
            />
          </div>
          {/* <div className='tw-w-[15%]'>
                    <label className='tw-text-sm tw-text-[#747593] tw-block'>{TYPE}</label>
                    <select className="drop-down-dm" value={selectedRuleType} onChange={handleRuleTypeChange} >
                        <option>
                            {RULE_SELECT}
                        </option>
                        {['CBM'].map((item, index) => {
                            return (
                                <option value={item} key={item + index}>
                                    {item}
                                </option>
                            );
                        })}
                    </select>
                </div> */}
          {/* <div className='tw-w-[10%]'>
                    <label className='tw-text-sm tw-text-[#747593] tw-block tw-mb-2'>{ASYMMETRIC}</label>
                    <ToggleButton
                        name="Required" 
                        value={false} 
                        onChange = {(e) => {}} />
                </div> */}
        </div>
        <div className="tw-flex tw-items-center tw-gap-4">
          <div className="tw-w-[47%] tw-mt-4">
            <label className="tw-text-sm tw-text-[#747593] tw-block">
              {DESCRIPTION}
            </label>
            <input
              className="input-field-dm"
              placeholder={ENTER}
              type="text"
              onChange={(e) => {
                onInputChangeHandler(e, RULE_FUNCTION_DESCRIPTION);
              }}
              value={cbmRule.description}
            />
          </div>
        </div>
        <div className="tw-flex tw-w-full tw-h-[92px] tw-bg-shades-of-grey-10 tw-justify-center tw-items-center tw-rounded-md border tw-border-zinc-200 tw-mt-4 tw-gap-4">
          <div className="tw-w-[20%] ">
            <label className="tw-text-sm tw-text-[#747593] tw-block">
              {SUB_MODEL_TYPE}
            </label>
            <select
              className="drop-down-dm"
              value={
                selectedLeftDataModel
                  ? selectedLeftDataModel?.id ?? RULE_EMPTY_STRING
                  : RULE_EMPTY_STRING
              }
              onChange={(e) => handleLeftDataModelSelect(e.target.value)}
            >
              <option>{RULE_SELECT}</option>
              {dataModels?.map((item, index) => {
                return (
                  <option value={item?.id ?? RULE_EMPTY_STRING} key={item?.id}>
                    {item?.data?.name ?? RULE_EMPTY_STRING}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="tw-w-[20%] ">
            <label className="tw-text-sm tw-text-[#747593] tw-block">
              {FIELDS}
            </label>
            <select
              className="drop-down-dm"
              value={
                selectedLeftSideField
                  ? selectedLeftSideField?.id ?? RULE_EMPTY_STRING
                  : RULE_EMPTY_STRING
              }
              onChange={(e) => handleLeftSideFieldSelect(e.target.value)}
            >
              <option>{RULE_SELECT}</option>
              {leftSideFields?.map((item, index) => {
                return (
                  <option value={item?.id ?? RULE_EMPTY_STRING} key={item?.id}>
                    {item?.name ?? RULE_EMPTY_STRING}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="tw-w-[12%]">
            <select
              className="drop-down-dm tw-mt-6"
              value={cbmRule?.operator ?? RULE_EMPTY_STRING}
              onChange={handleOperatorChange}
            >
              <option>{RULE_SELECT}</option>
              {["Default to", "Equal to"].map((item, index) => {
                return (
                  <option value={item} key={item + index}>
                    {item}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="tw-w-[20%]">
            <label className="tw-text-sm tw-text-[#747593] tw-block">
              {SUB_MODEL_TYPE}
            </label>
            <select
              className="drop-down-dm"
              value={
                selectedRightDataModel
                  ? selectedRightDataModel?.id ?? RULE_EMPTY_STRING
                  : RULE_EMPTY_STRING
              }
              onChange={(e) => handleRightDataModelSelect(e.target.value)}
            >
              <option>{RULE_SELECT}</option>
              {dataModels?.map((item, index) => {
                return (
                  <option value={item?.id ?? RULE_EMPTY_STRING} key={item?.id}>
                    {item?.data?.name ?? RULE_EMPTY_STRING}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="tw-w-[20%]">
            <label className="tw-text-sm tw-text-[#747593] tw-block">
              {FIELDS}
            </label>
            <select
              className="drop-down-dm"
              value={
                selectedRightSideField
                  ? selectedRightSideField?.id ?? RULE_EMPTY_STRING
                  : RULE_EMPTY_STRING
              }
              onChange={(e) => handleRightSideFieldSelect(e.target.value)}
            >
              <option>{RULE_SELECT}</option>
              {rightSideFields?.map((item, index) => {
                return (
                  <option value={item?.id ?? RULE_EMPTY_STRING} key={item?.id}>
                    {item?.name ?? RULE_EMPTY_STRING}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        {selectedRightSideField ? (
          <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
            <div className="tw-flex tw-mt-3 tw-items-center tw-gap-3 tw-min-w-[50%]">
              {/* <label className='tw-text-[#090A42] tw-font-semibold tw-text-sm'>{RATING_LEVELS}</label>
                        <div className='tw-w-[20%]'>
                            <select className="drop-down-dm" value={ ''} onChange={() => {}} >
                                <option>
                                    {RULE_SELECT}
                                </option>
                                {['Level 2'].map((item, index) => {
                                    return (
                                        <option value={item} key={item + index}>
                                            {item}
                                        </option>
                                    );
                                })}
                            </select>
                        </div> */}
              <div className="tw-flex tw-items-center tw-gap-2">
                <EmptyCircle />
                <label className="tw-text-[#747593} tw-text-sm">
                  {INCOMPATIBLE}
                </label>
              </div>
              <div className="tw-flex tw-items-center tw-gap-2">
                <FilledCircle />
                <label className="tw-text-[#747593} tw-text-sm">{BEST}</label>
              </div>
            </div>

            {/* <div className='tw-flex tw-mt-3 tw-items-center tw-gap-3'>
                        <label className='tw-text-[#747593} tw-text-sm'>{DEFAULT_RATING}</label>
                        <div className='tw-w-[50%]'>
                            <select className="drop-down-dm" value={ ''} onChange={() => {}} >
                                <option>
                                    {RULE_SELECT}
                                </option>
                                {[INCOMPATIBLE, BEST].map((item, index) => {
                                    return (
                                        <option value={item} key={item + index}>
                                            {item}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    </div> */}
          </div>
        ) : null}
        <MatrixTable
        leftSideAttributeValues={selectedLeftSideAttributeValues}
        rightSideAttributeValues={selectedRightSideAttributeValues}
        cbmRule={cbmRule}
        setCbmRule={setCbmRule}
      />
      {selectedRightSideField && (
        <button
          onClick={onSave}
          className={`tw-mt-2 tw-rounded  tw-bg-shades-of-grey-0 tw-text-primary tw-flex tw-flex-row tw-items-center tw-justify-center tw-h-7 tw-py-1 tw-px-2.5 tw-gap-[4px] tw-text-sm  tw-border-[1px] tw-border-solid tw-border-primary`}
        >
          {RULE_SAVE}
        </button>
      )}
      </div>

    </React.Fragment>
  );
};

export default CBMRule;
